import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Box, Button, Typography, Paper, Link, Switch, Stack, Chip, Snackbar, Alert, Backdrop, CircularProgress } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { close_trip_group, get_trip_group_all } from "../../api/API";
import axios from "axios";
import { jwtDecode } from "jwt-decode";

const IOSSwitch = styled((props) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
    width: 46,
    height: 26,
    padding: 0,
    display: "flex",
    "& .MuiSwitch-switchBase": {
        padding: 1.2,
        "&.Mui-checked": {
            transform: "translateX(20px)",
            color: "#fff",
            "& + .MuiSwitch-track": {
                backgroundColor: "#2d6a4f",
                opacity: 1,
            },
        },
    },
    "& .MuiSwitch-thumb": {
        boxShadow: "0 2px 4px 0 rgb(0 0 0 / 20%)",
        width: 22,
        height: 22,
        borderRadius: 11,
    },
    "& .MuiSwitch-track": {
        borderRadius: 26 / 2,
        opacity: 1,
        backgroundColor: theme.palette.mode === "dark" ? "#8796A5" : "#cfd8dc",
    },
}));

function MainJoinTripPage() {
    const navigate = useNavigate();
    const token = localStorage.getItem("token");
    const decoded = token ? jwtDecode(token) : {};
    const currentUID = decoded?.uid || decoded?.user_id || null;

    const [dataTripGroup, setDataTripGroup] = useState([]);
    const [initialLoading, setInitialLoading] = useState(true);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            if (!token) return;
            setInitialLoading(true);
            try {
                const response = await axios.get(get_trip_group_all, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setDataTripGroup(response.data);
            } catch (err) {
                setSnackbarMessage("❌ โหลดข้อมูลล้มเหลว");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
            } finally {
                setInitialLoading(false);
            }
        };
        fetchData();
    }, [token]);

    // Memoize trips to avoid re-render on scroll
    const trips = useMemo(() => {
        return dataTripGroup?.map((trip) => ({
            id: trip?.id,
            uid: trip?.uid,
            title: `${trip?.district?.name} (${trip?.travel_type?.name})`,
            detail: trip?.descriptions,
            num_people: trip?.num_people,
            flag_active: trip?.flag_active,
            status: trip?.status,
            travel_type: trip?.travel_type?.name,
            path: trip?.path,
        }));
    }, [dataTripGroup]);

    // Toggle active status
    const handleToggleStatus = useCallback(
        async (tripId) => {
            try {
                const response = await axios.post(
                    close_trip_group,
                    { id: tripId },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setDataTripGroup((prev) =>
                    prev.map((trip) =>
                        trip.id === tripId ? { ...trip, ...response.data.data } : trip
                    )
                );
            } catch (error) {
                console.error("Toggle failed:", error);
                setSnackbarMessage("❌ เกิดข้อผิดพลาดในการเปลี่ยนสถานะ");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
            }
        },
        [token]
    );
    const handleJoinTrip = useCallback(
        (id, index, tripData) => {
            navigate("/tour/join-trip/detail", { state: { trip: tripData } });
        },
        [navigate]
    );

    const handleSnackbarClose = useCallback((event, reason) => {
        if (reason === "clickaway") return;
        setSnackbarOpen(false);
    }, []);


    return (
        <>
            <Backdrop
                sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 10 }}
                open={loading || initialLoading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    height: "calc(100vh - 80px)", // 100vh ลบส่วนปุ่มด้านล่าง
                    gap: 3,
                }}
            >

                <Typography variant="h4" fontWeight="bold" color="green" sx={{ mt: 3 }}>
                    🌿 เข้าร่วมทริป
                </Typography>

                <Box
                    sx={{
                        flex: 1,
                        width: "100%",
                        maxWidth: 900,
                        overflowY: "auto",
                        px: 2, // padding ข้าง
                        py: 2,
                        display: "flex",
                        flexDirection: "column",
                        gap: 3,
                    }}
                >
                    {trips.map((trip, index) => {
                        return (
                            <Paper
                                key={trip.id}
                                elevation={3}
                                sx={{
                                    p: 3,
                                    borderRadius: 3,
                                    backgroundColor: "rgba(255,255,255,0.8)",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    gap: 2,
                                }}
                            >
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" fontWeight="bold" color="green">
                                        {trip.title}
                                    </Typography>
                                    <Link
                                        // href="#"
                                        underline="hover"
                                        sx={{ fontSize: "0.9rem", color: "green", display: "block", mt: 0.5 }}
                                    >
                                        รายละเอียดทริป
                                    </Link>
                                </Box>

                                <Box
                                    sx={{
                                        flex: 1,
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        alignItems: "center",
                                        gap: 2,
                                    }}
                                >
                                    {/* กล่องสถานะ + สวิตช์ */}
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mr: 2 }}>
                                        <Chip
                                            label={trip.flag_active === true ? "กำลังทำงาน" : "ปิดใช้งาน"}
                                            sx={{
                                                fontWeight: "bold",
                                                fontSize: "0.85rem",
                                                backgroundColor: trip.flag_active === true ? "#2d6a4f" : "#adb5bd",
                                                color: "#fff",
                                            }}
                                        />
                                        <IOSSwitch
                                            checked={trip.flag_active}
                                            onChange={() => handleToggleStatus(trip.id)}
                                            disabled={trip.uid !== Number(currentUID)}
                                            sx={{
                                                opacity: trip.uid !== Number(currentUID) ? 0.4 : 1,
                                                pointerEvents: trip.uid !== Number(currentUID) ? "none" : "auto",
                                            }}
                                        />

                                    </Stack>

                                    {/* กล่องรายละเอียด */}
                                    <Box
                                        sx={{
                                            backgroundColor: "green",
                                            color: "white",
                                            px: 3,
                                            py: 1.5,
                                            borderRadius: 1,
                                            fontWeight: "bold",
                                            textAlign: "center",
                                            flex: 1,
                                        }}
                                    >
                                        {trip.detail}
                                    </Box>

                                    {/* ปุ่มเข้าร่วมทริป */}
                                    <Button
                                        variant="contained"
                                        disabled={!trip.flag_active} // ❌ ปิดใช้งานถ้าทริปไม่เปิดอยู่
                                        onClick={() => handleJoinTrip(trip.id, index, trip)}
                                        sx={{
                                            backgroundColor: !trip.flag_active ? "#ccc" : "#f5f5f5",
                                            color: !trip.flag_active ? "#666" : "black",
                                            borderRadius: 3,
                                            border: "1px solid #2d6a4f",
                                            px: 3,
                                            py: 1,
                                            "&:hover": {
                                                backgroundColor: trip.flag_active ? "#d8f3dc" : "#ccc",
                                            },
                                        }}
                                    >
                                        เข้าร่วมทริป
                                    </Button>
                                </Box>
                            </Paper>
                        )
                    })}
                </Box>

                <Button
                    variant="contained"
                    onClick={() => navigate("/tour")}
                    sx={{
                        mt: 5,
                        background: "linear-gradient(135deg, #07ae26ff, #52b788)",
                        color: "white",
                        fontWeight: "bold",
                        borderRadius: 3,
                        px: 4,
                        py: 1.2,
                        boxShadow: "4px 4px 10px rgba(0,0,0,0.2)",
                        "&:hover": {
                            background: "linear-gradient(135deg, #95d5b2, #74c69d)",
                            transform: "translateY(-2px)",
                            boxShadow: "6px 6px 14px rgba(0,0,0,0.25)",
                        },
                        transition: "all 0.2s ease-in-out",
                    }}
                >
                    ⬅️ กลับหน้าหลัก
                </Button>
            </Box>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={4000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: "100%" }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
}

export default MainJoinTripPage;
